import { useSelector } from 'react-redux';
import type { RootState } from '../redux/store';

/**
 * useRole — reads activeRole + user properties from Redux.
 *
 * Replaces all direct localStorage.getItem('activeRole') calls in components.
 * Convenience booleans match the backend Policy boundaries:
 *   canHRAccess      → [Authorize(Policy = "HRAccess")]        Admin | HR | Accountant
 *   canFinanceAccess → [Authorize(Policy = "FinanceAccess")]    Admin | Accountant
 *   canRecruit       → [Authorize(Policy = "RecruitmentAccess")]Admin | Recruiter
 *   canEmployeePortal→ [Authorize(Policy = "EmployeePortal")]   Employee | Admin | HR | Accountant
 *
 * @example
 *   const { isHR, canHRAccess, isManager } = useRole();
 */
export const useRole = () => {
  const { activeRole, user, employeeId, userId } = useSelector(
    (s: RootState) => s.auth as any,
  );

  const role = (
    activeRole ||
    user?.role ||
    localStorage.getItem('activeRole') ||
    ''
  )
    .toLowerCase()
    .trim();

  // All roles the user *holds* (independent of the currently active role).
  const heldRoles: string[] = (Array.isArray(user?.roles) ? user!.roles : [])
    .map((r: string) => String(r).toLowerCase().trim())
    .filter(Boolean);

  return {
    role,
    // Every role the user holds, lower-cased.
    roles: heldRoles,
    // True when the user holds Admin, regardless of which role is active.
    hasAdminRole: heldRoles.includes('admin') || role === 'admin',
    // Individual role flags:
    isAdmin: role === 'admin',
    isHR: role === 'hr',
    isEmployee: role === 'employee',
    isRecruiter: role === 'recruiter',
    isAccountant: role === 'accountant',
    isCandidate: role === 'candidate',
    // User properties:
    isManager: user?.isManager ?? false,
    isTeamLead: user?.isTeamLead ?? false,
    // IDs (prefer Redux over DecodeToken):
    employeeId: employeeId ?? null,   // primarysid — for EM/payroll API calls
    userId: userId ?? null,           // nameid    — for UM API calls
    // Backend-policy aligned convenience flags:
    canHRAccess: ['admin', 'hr', 'accountant'].includes(role),
    canFinanceAccess: ['admin', 'accountant'].includes(role),
    canRecruit: ['admin', 'recruiter'].includes(role),
    canEmployeePortal: ['employee', 'admin', 'hr', 'accountant'].includes(role),
    canAdminOnly: role === 'admin',
  };
};
